<template>
  <component
    :is="component"
    :class="['stroke-n', 'neutral-50', 'outlined']"
    class="button-shelf"
    v-bind="props"
    @mouseover="handleMouseOver"
    @mouseleave="handleMouseLeave"
    @focus="handleMouseOver"
    @blur="handleMouseLeave"
    @click="handleClick"
  >
    <span class="solid" />
    <span class="logo" :style="styleLogo" />
    <span class="logo-tent" :style="styleTent" />
    <span :style="style" class="content">
      <h3>{{ title }}</h3>
    </span>
  </component>
</template>

<script lang="ts" setup>
import type { RouteLocationRaw } from 'vue-router';
interface Props {
  color?: string;
  to?: string | RouteLocationRaw;
  title: string;
}
const props = defineProps({
  color: { default: '#fff' },
  to: null,
  title: null
});

const { libraryStrategy } = inject(INJECT_LIBRARY_PAGE_OR_FORM);

const component = computed(() => {
  if (libraryStrategy.type === 'page') {
    return resolveComponent('NuxtLink');
  }
  return 'button';
});

const color = computed(() => props.color);

const { style, scale } = useScaleEffect(1);

const { scale: scaleLogo, style: styleLogo } = useScaleEffect(
  0.95,
  false,
  5.5,
  50,
  0.1
);
const { scale: scaleTent, style: styleTent } = useScaleEffect(
  0.95,
  false,
  10,
  100,
  0.4
);

const handleMouseOver = () => {
  scale.value = 1.2;
  scaleLogo.value = 1.1;
  scaleTent.value = 1;
};
const handleMouseLeave = () => {
  scale.value = 1;
  scaleLogo.value = 0.95;
  scaleTent.value = 0.95;
};

const handleClick = () => {
  if (libraryStrategy.type === 'form') {
    libraryStrategy.setRoute(props.to as string);
  }
};
</script>
<style lang="scss" scoped>
.solid {
  position: absolute;
  z-index: 1;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transition: opacity 200ms $ease-in-quad;
  opacity: 0;
  background: v-bind(color);
  filter: brightness(80%) saturate(150%);
}
.button-shelf {
  @include font('s', '500');

  display: flex;
  position: relative;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  overflow: hidden;

  // padding: 2rem;
  transition: box-shadow 300ms $ease-out-quad;
  border: 0;
  background: v-bind(color);
  box-shadow: rgb(0 0 0 / 0%) 0 6px 16px;
  aspect-ratio: 10/14;

  @include above('l') {
    aspect-ratio: 10/14;
  }
  &:focus,
  &:hover {
    transition: box-shadow 200ms $ease-in-quad;
    box-shadow: rgb(0 0 0 / 10%) 0 1px 3px 0, rgb(0 0 0 / 6%) 0 1px 2px 0;
    .solid {
      opacity: 1;
    }
  }
}

.content {
  display: flex;
  position: relative;
  z-index: 1;
  flex-basis: 50%;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  font-size: var(--text_size_l);
  text-align: center;
}

.logo {
  position: absolute;
  z-index: 1;
  bottom: 10%;
  width: 14rem;
  height: 4.8rem;
  margin: 0 auto;
  transform-origin: 50% 100%;
  opacity: 1;
  @include background('/images/logos/logo-white', 'png', 140px, 48px);
}

.logo-tent {
  position: absolute;
  z-index: 1;
  bottom: 0;
  width: 12rem;
  height: 12rem;
  margin: 0 auto;
  transform-origin: 50% 100%;
  opacity: 0.5;
  @include background(
    '/images/logos/logo_nomad_tent-mobile',
    'png',
    120px,
    120px
  );
  @include above('m') {
    width: 14rem;
    height: 14rem;
    margin: 0 auto;
    opacity: 0.5;
    @include background('/images/logos/logo_nomad_tent', 'png', 140px, 140px);
  }
}

h3 {
  display: -webkit-box;
  overflow: hidden;
  color: var(--color_neutral_25);
  text-overflow: ellipsis;
  white-space: normal;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  @include font('l', 800, 'secondary');
}
</style>
